import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Send, Eye, FileText, CheckCircle2, Flame, Link2, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { KPICard } from "./KPICard";
import { SectionHeader } from "./HelperText";
import EmailFunnelCard from "./EmailFunnelCard";
import EmailRecentActivityCard from "./EmailRecentActivityCard";
import EmailHealthCard from "./EmailHealthCard";
import { useEmailDashboardData } from "../../hooks/useEmailDashboardData";
import { useEmailCorrelationEvidenceData } from "../../hooks/useEmailCorrelationEvidenceData";

// Checkpoint 3.1: MissionAssist Email exists to drive
// Email -> Landing Page -> Quote -> High Intent -> Application -> Revenue.
// Checkpoint 3.3: Executive Summary's job is efficiency ("how well is the
// program performing" — rates), not raw progression counts, which the
// Acquisition Funnel immediately below already shows. Provider engagement
// metrics (open/click rate) still stay out of this surface — those belong
// to Performance/Creative views, not Overview.
//
// EMAIL-DASHBOARD-KPI-001: all five cards are now real (see
// useEmailDashboardData.js's own buildRealKpis). `emails_sent`/
// `high_intent_rate` were renamed to `emails_processed`/
// `high_intent_actions_per_quote_complete` -- the former to name what the
// underlying fact actually is (a provider-processed-confirmation count,
// not a send-attempt fact), the latter because High Intent Actions is a
// frozen, deliberately non-deduplicated EVENT count while Quote Completes
// is a distinct-identity count -- their ratio is an event-frequency
// intensity metric, not a prospect-conversion rate, so it is never
// percent-formatted and is never called a "rate" (see that KPI's own
// `format: "number"` below and its methodology tooltip). `methodology`
// (true) marks which cards get an info-icon tooltip in the render loop
// below -- Quote Completion Rate has none, since Quote Completes/Quote
// Starts share one analytical grain and need no cross-grain caveat.
const KPI_DEFS = [
  { key: "emails_processed", title: "Emails Processed", format: "number", icon: Send },
  { key: "delivery_rate", title: "Delivery Rate", format: "percent", icon: CheckCircle2, methodology: true },
  { key: "landing_page_view_rate", title: "Landing Page View Rate", format: "percent", icon: Eye, methodology: true },
  { key: "quote_completion_rate", title: "Quote Completion Rate", format: "percent", icon: FileText },
  {
    // EMAIL-DASHBOARD-KPI-003: display label only -- shortened from "High
    // Intent Actions per Quote Complete" to "High Intent Per Quote
    // Complete" for Preview title-layout polish. The KPI key, calculation
    // (High Intent Actions / Quote Completes), numerator, denominator,
    // format ("number", never percent), and methodology tooltip copy are
    // all unchanged.
    key: "high_intent_actions_per_quote_complete",
    title: "High Intent Per Quote Complete",
    format: "number",
    icon: Flame,
    methodology: true,
  },
];

// EMAIL-DASHBOARD-KPI-001: relocated from a permanent block above the
// Executive Summary (see this file's own git history) into
// EmailFunnelCard.jsx's info-icon tooltip beside its "Acquisition Funnel"
// heading -- verbatim text, no semantic rewrite.
const ACQUISITION_FUNNEL_METHODOLOGY_DISCLOSURE =
  "Period, Environment, Broadcast, Audience, Program, Campaign, and State now apply across the entire Acquisition Funnel. Delivered uses MESSAGE-grain EDT facts; Processed also uses MESSAGE-grain EDT facts, timed independently from Delivered (see each stage's own tooltip); the funnel stages below them use each event's own resolved origin attribution. High Intent Actions is a raw qualifying-event count, expandable into six business-action buckets. Applications Submitted and Inforce Policies are placeholders only -- tracking is not yet connected for either. Environment means different provenance in each case (GHL discovery environment for Delivered; the Broadcast's own configured environment for the funnel stages) -- shared filtering does not mean these share one entity grain. GHL Discovery Health and Correlation Evidence are account-level current snapshots and are not affected by these filters.";

// DASH-EMAIL-025: a tenant can now have more than one real Processing
// Health checkpoint (one per governed discovery source), so each
// EmailHealthCard rendered below needs its own label distinguishing which
// source it describes. Computed HERE (not inside EmailHealthCard.jsx
// itself, which stays a minimal, label-agnostic renderer) -- prefers the
// resolved, human-readable environment ("Qualification"/"Production"),
// falling back to the raw ghl_location_id only when that environment
// couldn't be resolved (see email_delivery_processing_health_service.py's
// own docstring for why environment resolution is a best-effort, display-
// only lookup that can legitimately come back empty).
function formatProcessingHealthSourceLabel(source) {
  if (source?.environment) {
    const raw = String(source.environment);
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  return source?.ghlLocationId || null;
}

function EmailKpiGridSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="email-kpi-skeleton">
      {KPI_DEFS.map((def) => (
        <Card key={def.key} className="glass border-white/10 animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="h-3 w-20 rounded bg-white/10" />
            <div className="h-6 w-16 rounded bg-white/10" />
            <div className="h-3 w-24 rounded bg-white/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * EmailOverviewView — the Email tab's default view (Checkpoint 3.1).
 *
 * Answers "is our email acquisition engine creating qualified
 * opportunities?" via four sections: Executive Summary (KPI row),
 * Acquisition Funnel, Recent Activity, and Processing Health. All four are
 * driven by a single useEmailDashboardData call, so loading/error/empty
 * states are shared and consistent across the whole view.
 *
 * Checkpoint 3.4E: Overview now also receives customRange — the unified
 * Email filter bar (EmailFilterBar.jsx, owned by EmailDashboardPanel)
 * applies to both Overview and Performance identically now, and these are
 * always *applied* (never draft) values.
 *
 * DASH-EMAIL-P1-IMPL-006: the top filter bar's Environment/Broadcast
 * controls (plus State, moved into its Advanced row) are now Overview's
 * Delivered filters — the previously-separate "Governed Delivered
 * Filters" card has been removed. `environment` doubles as
 * `observedGhlEnvironment` for the real EDT fetch since the top control's
 * value space ("all"|"qualification"|"production") is byte-identical to
 * that real, governed filter's — no separate selector or local state is
 * needed for it.
 *
 * DASH-EMAIL-P1-IMPL-032: `governedBroadcastId`/`governedAudienceId`/
 * `governedProgramId`/`governedCampaignId`/`governedState` arrive as
 * applied props from EmailDashboardPanel (backed by the real governed
 * Broadcast/Audience-Definition/Program-Campaign catalogs and the
 * canonical state vocabulary) and now reach BOTH real fetches (EDT
 * `/summary` and LPE `/lpe-funnel`) — see useEmailDashboardData.js's own
 * header comment for the exact per-domain parameter mapping, including
 * why `environment` maps to a DIFFERENT parameter name on each domain.
 * The mock-catalog-driven `program`/`campaign` props this view used to
 * also accept are retired for Overview specifically — its own
 * `EMAIL_FILTER_BAR_FIELDS.overview` config now renders the governed
 * selectors in that exact same Advanced-row slot instead (mutually
 * exclusive by construction, matching `governedBroadcastId`'s own
 * existing precedent) — see EmailFilterBar.jsx's own comment on that
 * config.
 *
 * DASH-EMAIL-011: Overview also hosts Correlation Evidence — a
 * MESSAGE-grain read (email_delivery_messages.correlation_status), the
 * SAME grain as this view's own real Delivered stat. Placed here (not
 * Deliverability, which hosts RECIPIENT-grain hygiene/DND) precisely
 * because grain, not visual category, decides placement: Overview is
 * already the sole MESSAGE-grain-sourced surface (Delivered), and
 * Correlation Evidence answers a closely related question ("is the
 * message<->SendGrid-event tracking pipeline collecting evidence?") right
 * alongside Processing Health's CHECKPOINT-grain ("is the discovery
 * pipeline running?") — both describe pipeline mechanics, never a
 * recipient-facing consent/hygiene state. Fetched independently
 * (useEmailCorrelationEvidenceData) so a failure here never blocks any
 * other section, and vice versa.
 */
export default function EmailOverviewView({
  period,
  environment,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
}) {
  const {
    loading,
    error,
    kpis,
    funnel,
    recentBroadcastPerformance,
    processingHealthSources,
    attributionCoverage,
    highIntentBreakdown,
  } = useEmailDashboardData({
    period,
    environment,
    customRange,
    governedBroadcastId: governedBroadcastId === "all" ? null : governedBroadcastId,
    governedAudienceId: governedAudienceId === "all" ? null : governedAudienceId,
    governedProgramId: governedProgramId === "all" ? null : governedProgramId,
    governedCampaignId: governedCampaignId === "all" ? null : governedCampaignId,
    governedState: governedState === "all" ? null : governedState,
    observedGhlEnvironment: environment === "all" ? null : environment,
    lpeEnvironment: environment === "all" ? null : environment,
  });
  const correlationEvidence = useEmailCorrelationEvidenceData();
  const hasKpis = kpis && Object.keys(kpis).length > 0;

  // DASH-EMAIL-P1-IMPL-032: whether at least one governed attribution
  // filter is actively applied -- Period alone does not count (Period has
  // always applied across the whole funnel, filtered or not; the coverage
  // disclosure is specifically about GOVERNED ATTRIBUTION filters).
  const hasActiveGovernedFilter =
    (environment && environment !== "all") ||
    (governedBroadcastId && governedBroadcastId !== "all") ||
    (governedAudienceId && governedAudienceId !== "all") ||
    (governedProgramId && governedProgramId !== "all") ||
    (governedCampaignId && governedCampaignId !== "all") ||
    (governedState && governedState !== "all");

  const noAttributionRecordEvents = attributionCoverage?.no_attribution_record_events;
  const totalPeriodEvents = attributionCoverage?.total_period_events;
  // DASH-EMAIL-P1-IMPL-032: shown ONLY when a governed attribution filter
  // is actively applied AND at least one period event has no attribution
  // record at all -- explanatory metadata only, never a KPI, never a
  // derived "fully attributed" percentage (see
  // EmailDeliveryLpeFunnelAttributionCoverage's own backend docstring for
  // why: the unresolved populations overlap and are not subtractable).
  // Missing attribution NEVER suppresses the real filtered funnel numbers
  // above -- this is purely an additional explanation alongside them.
  const showAttributionCoverageNotice =
    hasActiveGovernedFilter &&
    typeof noAttributionRecordEvents === "number" &&
    noAttributionRecordEvents > 0;

  return (
    <div className="space-y-6">
      {error && (
        <Card className="glass border-red-500/20" data-testid="email-dashboard-error">
          <CardContent className="p-4 text-sm text-red-300">
            Failed to load Email metrics: {error}
          </CardContent>
        </Card>
      )}

      {!error && loading && <EmailKpiGridSkeleton />}

      {!error && !loading && hasKpis && (
        <>
          {/* EMAIL-DASHBOARD-KPI-001: the persistent disclosure block that
              used to render here (Period/Environment/.../State now apply
              across the whole Acquisition Funnel, MESSAGE-grain timing
              notes, etc.) has moved verbatim into EmailFunnelCard.jsx's own
              info-icon tooltip beside its "Acquisition Funnel" heading --
              see ACQUISITION_FUNNEL_METHODOLOGY_DISCLOSURE above and the
              `methodologyDisclosure` prop passed to it below. It no longer
              consumes a permanent row on this page. */}

          <div>
            {/* EMAIL-DASHBOARD-KPI-001: the "Efficiency view..." subtitle
                is retired -- no persistent replacement subtitle is added in
                its place. */}
            <SectionHeader title="Executive Summary" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="email-kpi-grid">
              {KPI_DEFS.map((def) => {
                const metric = kpis[def.key];
                // EMAIL-DASHBOARD-KPI-002: `availability`/`availabilityReason`
                // are deliberately NOT passed to KPICard below -- this hides
                // the visible "UNAVAILABLE" badge KPICard's own
                // AvailabilityBadge would otherwise render, while the
                // internal availability semantics remain fully intact on
                // `metric` itself (useEmailDashboardData.js's own kpis
                // object, unaffected by this presentation choice). KPICard
                // already renders "—" for value === undefined regardless of
                // `availability` -- see that component's own displayValue
                // branches -- so the em-dash presentation is preserved with
                // no calculation change of any kind.
                const methodologyIcon =
                  def.methodology && metric?.methodology ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex text-slate-500 hover:text-slate-300 cursor-help"
                            aria-label={`About ${def.title}`}
                            data-testid={`email-kpi-${def.key}-methodology-info`}
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-sm text-xs"
                          data-testid={`email-kpi-${def.key}-methodology-tooltip`}
                        >
                          {metric.methodology}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null;
                return (
                  <KPICard
                    key={def.key}
                    title={def.title}
                    value={metric?.value}
                    format={def.format}
                    icon={def.icon}
                    titleAddon={methodologyIcon}
                  />
                );
              })}
            </div>
          </div>

          <EmailFunnelCard
            funnel={funnel}
            highIntentBreakdown={highIntentBreakdown}
            methodologyDisclosure={ACQUISITION_FUNNEL_METHODOLOGY_DISCLOSURE}
          />

          {/* DASH-EMAIL-P1-IMPL-032: Attribution Coverage -- explanatory
              metadata only, never a KPI/chart/funnel-stage/score. Shown
              ONLY when a governed attribution filter is active AND at
              least one period event has no attribution record at all
              (see showAttributionCoverageNotice's own comment above).
              States the raw X-of-Y relationship, never a derived
              percentage or a "fully attributed" figure -- the unresolved
              populations this could be computed from overlap (see
              EmailDeliveryLpeFunnelAttributionCoverage's own backend
              docstring). Never hides/suppresses the real filtered funnel
              numbers above -- purely additive context. */}
          {showAttributionCoverageNotice && (
            <div
              className="rounded-md border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-200"
              data-testid="email-attribution-coverage-notice"
            >
              Some funnel events in this period do not have an origin-attribution record (
              {noAttributionRecordEvents.toLocaleString()} of {totalPeriodEvents?.toLocaleString() ?? "?"} events), so
              they cannot match governed attribution filters.
            </div>
          )}

          <div>
            {/* DASH-EMAIL-006a (VIS-1): EmailHealthCard has no title of its
                own (it's an inline status indicator, not a titled Card) —
                this SectionHeader is the only heading either card in this
                pairing has, not a redundant restatement of one.
                RECENT-BROADCAST-PERFORMANCE-001 Preview follow-up: the two
                GHL Discovery Health cards render FIRST, sharing row 1 at
                desktop width, so a second discovery source never falls
                into an awkward row 2 beside empty space. Recent Broadcast
                Performance renders second and spans the full section width
                (`lg:col-span-2`) on its own row, matching Correlation
                Evidence's own full-width card below -- purely a DOM-order
                and grid-span change, no card's own internal layout,
                backend semantics, or filters are touched. At the
                single-column mobile breakpoint, `lg:col-span-2` is a no-op
                and every card simply stacks in this same DOM order. */}
            <SectionHeader title="Recent Activity & Processing Health" />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* DASH-EMAIL-025: one EmailHealthCard per real discovery-
                  source checkpoint (processingHealthSources always has at
                  least one entry -- see useEmailDashboardData.js's own
                  "never show nothing" discipline). Each card gets its own
                  sourceLabel so multiple cards are visually
                  distinguishable; data-testid is suffixed by the
                  location id (falling back to the array index if that's
                  ever absent) for test-ability. */}
              {processingHealthSources.map((source, index) => (
                <EmailHealthCard
                  key={source?.ghlLocationId || index}
                  health={source}
                  sourceLabel={formatProcessingHealthSourceLabel(source)}
                  testId={`email-health-card-${source?.ghlLocationId || index}`}
                />
              ))}
              <div className="lg:col-span-2">
                <EmailRecentActivityCard performance={recentBroadcastPerformance} />
              </div>
            </div>
          </div>

          {/* DASH-EMAIL-011: Correlation Evidence -- MESSAGE-grain read
              from email_delivery_messages.correlation_status, deliberately
              kept as its own section (not folded into the Recent
              Activity/Processing Health pairing above, which is an
              established, deliberate 2-card layout per DASH-EMAIL-006a/
              008B). No rate, no percentage, no health score -- correlated
              and awaiting-correlation are two independent counts, neither
              implying failure/stuck/stale/overdue for the latter, nor
              delivered/healthy for the former (see this checkpoint's own
              characterization for why the persisted correlation_status
              field cannot distinguish "never attempted" from "attempted
              but unresolved"). */}
          <div>
            <SectionHeader title="Correlation Evidence" />
            <Card className="glass border-white/10" data-testid="overview-correlation-evidence-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-indigo-400" />
                  Message Correlation
                </CardTitle>
                {/* DASH-EMAIL-P1-IMPL-010: local scope note, deliberately
                    short -- the full explanation (which filters this does
                    NOT respond to, and why) already lives in the
                    Acquisition Funnel's own methodology tooltip (see
                    EMAIL-DASHBOARD-KPI-001 above); this exists only so a
                    reader looking at this card in isolation isn't left to
                    assume it moves with Period/Environment/etc. */}
                <p className="text-xs text-slate-500" data-testid="correlation-evidence-scope-note">
                  Account-wide current snapshot
                </p>
              </CardHeader>
              <CardContent>
                {correlationEvidence.loading ? (
                  <div className="grid grid-cols-2 gap-4" data-testid="overview-correlation-evidence-loading">
                    <div className="h-12 rounded bg-white/10 animate-pulse" />
                    <div className="h-12 rounded bg-white/10 animate-pulse" />
                  </div>
                ) : correlationEvidence.error ||
                  correlationEvidence.evidenceStatus !== "available" ? (
                  <p className="text-sm text-slate-400" data-testid="overview-correlation-evidence-unavailable">
                    UNAVAILABLE
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p
                        className="text-2xl font-bold text-slate-100"
                        data-testid="overview-correlated-message-count"
                      >
                        {correlationEvidence.correlatedMessageCount.toLocaleString()}
                      </p>
                      <p className="text-sm text-slate-300 mt-1">Correlated Messages</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Messages with persisted SendGrid correlation evidence.
                      </p>
                    </div>
                    <div>
                      <p
                        className="text-2xl font-bold text-slate-100"
                        data-testid="overview-awaiting-correlation-message-count"
                      >
                        {correlationEvidence.awaitingCorrelationMessageCount.toLocaleString()}
                      </p>
                      <p className="text-sm text-slate-300 mt-1">Awaiting Correlation</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Messages without persisted SendGrid correlation evidence.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {!error && !loading && !hasKpis && (
        <Card className="glass border-white/10" data-testid="email-dashboard-empty">
          <CardContent className="p-8 text-center text-slate-400">
            <p className="text-lg">No Email data yet</p>
            <p className="text-sm mt-2">
              Metrics will appear here once Email data is available for this selection.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
